import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Truck, Check, ChevronRight, Headphones } from "lucide-react";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { useCart } from "@/context/CartContext";
import { useModals } from "@/context/ModalContext";
import { AddOnRow } from "@/components/AddOnRow";
import { Reviews } from "@/components/Reviews";
import { ProductCard } from "@/components/ProductCard";
import { ProductCompanions } from "@/components/recovery/ProductCompanions";

export default function ProductDetail() {
  const { handle } = useParams();
  const [data, setData] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [variant, setVariant] = useState(null);
  const [wgChecked, setWgChecked] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const { addItem } = useCart();
  const { openConsultation, openWhiteGlove, openQuote } = useModals();

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null);
    api.getProduct(handle).then((d) => {
      setData(d);
      setActiveImg(0);
      setActiveTab(0);
      setVariant(d.product.variants?.[0] || null);
      setWgChecked(false);
    }).catch(() => {});
  }, [handle]);

  if (!data) return <div className="pt-32 text-center text-cream/60 min-h-[60vh]">Loading…</div>;
  const { product, addons, essentials, alsoBought, reviews, reviewSummary, companions } = data;
  const tabs = product.tabs || [];

  const handleAdd = () => {
    addItem({
      productId: product.id,
      variantId: variant?.id,
      variantTitle: variant?.title,
      quantity: 1,
      isWhiteGlove: product.kohler && wgChecked,
    });
  };

  return (
    <div className="pt-16" data-testid="product-detail-page">
      <Seo title={`${product.title} | ${product.brand} | The Recovery Shops`}
        description={`${product.title} by ${product.brand}. ${(product.description || "Premium recovery equipment").slice(0, 130)} Request a quote and white-glove delivery from The Recovery Shops.`} />
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-8">
        <nav className="text-xs text-cream/50 flex items-center gap-1 mb-8">
          <Link to="/shop" className="hover:text-gold">Shop</Link><ChevronRight size={12} />
          <Link to={`/shop/${product.category}`} className="hover:text-gold capitalize">{product.category.replace("-", " ")}</Link>
          <ChevronRight size={12} /><span className="text-cream/70">{product.title}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Gallery */}
          <div>
            <div className="aspect-square overflow-hidden bg-charcoal group">
              <img data-testid="product-main-image" src={product.images[activeImg]} alt={product.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            </div>
            <div className="flex gap-3 mt-3">
              {product.images.map((img, i) => (
                <button key={i} data-testid={`thumb-${i}`} onClick={() => setActiveImg(i)}
                  className={`w-20 h-20 overflow-hidden border ${activeImg === i ? "border-gold" : "border-white/10"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div>
            <p className="overline">{product.brand}</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-cream mt-2 leading-tight">{product.title}</h1>
            <div className="flex items-center gap-3 mt-3">
              <span className="flex items-center gap-1 text-gold">
                <Star size={16} className="fill-gold" /> {reviewSummary.average}
              </span>
              <span className="text-cream/50 text-sm">({reviewSummary.count} reviews)</span>
            </div>
            <p className="text-cream/70 mt-5 leading-relaxed">{product.description}</p>

            {product.variants?.length > 1 && (
              <div className="mt-6">
                <p className="text-sm text-cream/70 mb-2">Options</p>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((v) => (
                    <button key={v.id} data-testid={`variant-${v.id}`} onClick={() => setVariant(v)}
                      className={`px-4 py-2 text-sm border transition-colors ${variant?.id === v.id ? "border-gold text-gold" : "border-white/15 text-cream/80 hover:border-cream/40"}`}>
                      {v.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* White glove */}
            {product.whiteGlove && (
              <div className="mt-6 border border-forest/30 bg-forest/10 p-4">
                <p className="flex items-center gap-2 text-forest font-medium text-sm"><Truck size={16} /> White Glove Delivery Available</p>
                {product.kohler ? (
                  <label className="flex items-center gap-2 mt-3 text-sm text-cream/80 cursor-pointer">
                    <input type="checkbox" data-testid="wg-toggle" checked={wgChecked}
                      onChange={(e) => setWgChecked(e.target.checked)} className="accent-gold w-4 h-4" />
                    Add White Glove Delivery
                  </label>
                ) : (
                  <button data-testid="wg-request-btn" onClick={() => openWhiteGlove(product)}
                    className="mt-3 text-sm text-amber underline">Request White Glove Delivery →</button>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 mt-7">
              <button data-testid="request-quote-btn" onClick={() => openQuote(product.title)}
                className="flex-1 bg-gold text-espresso py-4 font-medium hover:bg-amber transition-colors">
                Request a Quote
              </button>
              <button data-testid="add-to-cart-btn" onClick={handleAdd}
                className="flex items-center justify-center gap-2 border border-gold/50 text-gold px-6 py-4 hover:bg-gold/10 transition-colors">
                Add to Cart
              </button>
            </div>
            <button data-testid="talk-expert-btn" onClick={() => openConsultation(product.title)}
              className="mt-3 w-full flex items-center justify-center gap-2 border border-cream/25 text-cream/85 py-3 hover:border-cream transition-colors">
              <Headphones size={18} /> Talk to an Expert
            </button>

            {/* Specs */}
            <div className="mt-8 border-t border-white/10 pt-6">
              <h3 className="font-serif text-xl text-cream mb-4">Specifications</h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {Object.entries(product.specs || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-white/5 pb-2">
                    <dt className="text-cream/55 text-sm">{k}</dt>
                    <dd className="text-cream text-sm text-right">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>

        {/* Full product details tabs (from Shopify) */}
        {tabs.length > 0 && (
          <div className="mt-16" data-testid="product-tabs">
            <div className="flex gap-1 sm:gap-2 overflow-x-auto border-b border-white/10 snap-row">
              {tabs.map((t, i) => (
                <button key={t.title} data-testid={`tab-${i}`} onClick={() => setActiveTab(i)}
                  className={`shrink-0 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    activeTab === i ? "border-gold text-gold" : "border-transparent text-cream/60 hover:text-cream"}`}>
                  {t.title}
                </button>
              ))}
            </div>
            <div key={activeTab} className="rich mt-6 animate-fade-up max-w-4xl"
              data-testid={`tab-content-${activeTab}`}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(tabs[activeTab]?.html || "") }} />
          </div>
        )}

        {/* Add-ons */}
        <AddOnRow addons={addons} />

        {/* Compatibility-aware companions (sauna / heater) */}
        {companions ? (
          <ProductCompanions companions={companions} />
        ) : (
          essentials?.length > 0 && (
            <div className="mt-16" data-testid="essentials-section">
              <p className="overline">Recommended Essentials</p>
              <h2 className="font-serif text-2xl sm:text-3xl text-cream mt-2 mb-1">Complete Your Setup</h2>
              <p className="text-cream/55 text-sm mb-6">
                {product.category === "sauna"
                  ? "A compatible heater and the essentials for the full sauna ritual."
                  : "Everything you need to get the most from your setup."}
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {essentials.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            </div>
          )
        )}

        {/* Customers also bought */}
        {alsoBought?.length > 0 && (
          <div className="mt-16">
            <h2 className="font-serif text-2xl sm:text-3xl text-cream mb-6">You May Also Like</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {alsoBought.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}

        {/* Reviews */}
        <div className="mt-16">
          <Reviews summary={reviewSummary} reviews={reviews} />
        </div>
      </div>
    </div>
  );
}
